import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, Copy, Plus, X } from "lucide-react";
import { useCallback, useState } from "react";
import { EditUserDialog } from "../components/EditUserDialog.js";
import { InviteUserDialog } from "../components/InviteUserDialog.js";
import { UserTable } from "../components/UserTable.js";
import { useUsers } from "../hooks/useUsers.js";
import { useOrgAdminT } from "../i18n/context.js";
export function UsersPage({ renderUserActions, allowAcceptInvitation }) {
    const t = useOrgAdminT();
    const [statusFilter, setStatusFilter] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [invitationActionLoading, setInvitationActionLoading] = useState(null);
    const [error, setError] = useState(null);
    // Resend returns a fresh shareable URL synchronously — surface it in a
    // dismissible panel with copy-to-clipboard (the manual-share path for
    // +clerk_test addresses that get no real email). An email EDIT reuses the
    // same panel: it re-issues the invitation, so the operator's next move is
    // identical. `deliveryFailed` upgrades it from convenience to the recovery
    // path — the invitation exists but never reached anyone.
    const [resendUrl, setResendUrl] = useState(null);
    const [copied, setCopied] = useState(false);
    const { users, loading, reload, deleteUser, resendInvitation, revokeInvitation, acceptInvitation } = useUsers({
        status: statusFilter || undefined,
        role: roleFilter || undefined,
    });
    const handleDelete = useCallback(async (user) => {
        if (!confirm(t("users.deactivateConfirm", { name: user.displayName })))
            return;
        setDeleteLoading(user.userId);
        setError(null);
        try {
            await deleteUser(user.userId);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : t("users.failedDeactivate"));
        }
        finally {
            setDeleteLoading(null);
        }
    }, [deleteUser, t]);
    const handleResend = useCallback(async (user) => {
        setInvitationActionLoading(user.userId);
        setError(null);
        try {
            const res = await resendInvitation(user.userId);
            setResendUrl({ email: user.email, url: res.invitationUrl });
            setCopied(false);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : t("users.failedResend"));
        }
        finally {
            setInvitationActionLoading(null);
        }
    }, [resendInvitation, t]);
    const handleRevoke = useCallback(async (user) => {
        if (!confirm(t("users.revokeConfirm", { name: user.displayName })))
            return;
        setInvitationActionLoading(user.userId);
        setError(null);
        try {
            await revokeInvitation(user.userId);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : t("users.failedRevoke"));
        }
        finally {
            setInvitationActionLoading(null);
        }
    }, [revokeInvitation, t]);
    const handleAccept = useCallback(async (user) => {
        // Loud confirmation — accept-on-behalf bypasses user consent and
        // sets a well-known password. The operator must follow up with the
        // real user out-of-band. The text spells out the post-action step
        // so the operator can't claim they weren't warned.
        const ok = confirm(t("users.acceptConfirm", { name: user.displayName, email: user.email }));
        if (!ok)
            return;
        setInvitationActionLoading(user.userId);
        setError(null);
        try {
            await acceptInvitation(user.userId);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : t("users.failedAccept"));
        }
        finally {
            setInvitationActionLoading(null);
        }
    }, [acceptInvitation, t]);
    return (_jsxs("div", { children: [_jsxs("div", { className: "mb-4 flex items-center justify-between", children: [_jsxs("div", { className: "flex gap-3", children: [_jsxs("select", { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), className: "rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900", children: [_jsx("option", { value: "", children: t("users.allStatuses") }), _jsx("option", { value: "ACTIVE", children: t("users.statusActive") }), _jsx("option", { value: "PROVISIONING", children: t("users.statusProvisioning") }), _jsx("option", { value: "DEACTIVATED", children: t("users.statusDeactivated") })] }), _jsxs("select", { value: roleFilter, onChange: (e) => setRoleFilter(e.target.value), className: "rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900", children: [_jsx("option", { value: "", children: t("users.allRoles") }), _jsx("option", { value: "ORG_ADMIN", children: t("users.roleOrgAdmin") }), _jsx("option", { value: "MEMBER", children: t("users.roleMember") })] })] }), _jsxs("button", { type: "button", onClick: () => setShowInvite(true), className: "inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700", children: [_jsx(Plus, { className: "h-4 w-4" }), " ", t("users.addUser")] })] }), showInvite && (_jsx(InviteUserDialog, { onClose: () => setShowInvite(false), onCreated: () => { setShowInvite(false); reload(); } })), editing && (_jsx(EditUserDialog, { user: editing, onClose: () => setEditing(null), 
                // The write landed even though the request failed — refresh so the
                // row shows the address that is actually stored. The dialog stays
                // open with the error; do NOT close it, the operator has a recovery
                // step to read.
                onDataChanged: () => reload(), onSaved: ({ invitationUrl, email, emailDeliveryFailed }) => {
                    setEditing(null);
                    // Only raise the panel when there is a real URL to show —
                    // absence means none was produced, not an empty one.
                    if (invitationUrl) {
                        setResendUrl({ email, url: invitationUrl, deliveryFailed: emailDeliveryFailed });
                        setCopied(false);
                    }
                    else if (emailDeliveryFailed) {
                        // Delivery failed AND no URL to fall back on — say so rather
                        // than letting a silent non-delivery pass as a clean save.
                        setError(t("users.emailDeliveryFailedNoUrl", { email }));
                    }
                    reload();
                } })), error && (_jsx("div", { className: "mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400", children: error })), resendUrl && (_jsxs("div", { className: `mb-4 rounded-md border p-3 text-sm ${resendUrl.deliveryFailed
                    ? "border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30"
                    : "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/30"}`, children: [resendUrl.deliveryFailed && (_jsxs("div", { className: "mb-2 flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400", children: [_jsx(AlertTriangle, { className: "mt-0.5 h-3.5 w-3.5 shrink-0" }), _jsx("span", { children: t("users.emailDeliveryFailed", { email: resendUrl.email }) })] })), _jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsx("span", { className: "font-medium text-indigo-700 dark:text-indigo-300", children: t("users.freshInvitationUrl", { email: resendUrl.email }) }), _jsx("button", { type: "button", onClick: () => setResendUrl(null), className: "text-indigo-400 hover:text-indigo-600", "aria-label": t("common.dismiss"), children: _jsx(X, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("a", { href: resendUrl.url, target: "_blank", rel: "noopener noreferrer", title: resendUrl.url, className: "flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs text-indigo-700 underline decoration-dotted underline-offset-2 hover:text-indigo-900 dark:bg-slate-900 dark:text-indigo-300 dark:hover:text-indigo-200", children: resendUrl.url }), _jsxs("button", { type: "button", onClick: () => {
                                    // Only claim "Copied" when the write actually resolves —
                                    // clipboard is undefined in non-secure contexts and
                                    // writeText rejects on permission denial.
                                    navigator.clipboard
                                        ?.writeText(resendUrl.url)
                                        .then(() => setCopied(true))
                                        .catch(() => { });
                                }, className: "inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700", children: [_jsx(Copy, { className: "h-3 w-3" }), " ", copied ? t("common.copied") : t("common.copy")] })] })] })), _jsx(UserTable, { users: users, loading: loading, onDeleteUser: handleDelete, deleteLoading: deleteLoading, onEditUser: setEditing, onResendInvitation: handleResend, onRevokeInvitation: handleRevoke, onAcceptInvitation: allowAcceptInvitation ? handleAccept : undefined, invitationActionLoading: invitationActionLoading, renderActions: renderUserActions })] }));
}
//# sourceMappingURL=UsersPage.js.map